import SumoDidiMedia, { SumoDidiMediaLinks } from "../components/SumoDidiMedia";
import { withSumoDidiUpdates } from "../data/sumoDidiMedia";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { allWork, featureFilm as originalFeatureFilm, showreels } from "../data/mock";
import { ArrowRight, ExternalLink } from "lucide-react";

const WorkPage = () => {
  const featureFilm = withSumoDidiUpdates(originalFeatureFilm);
  const commercials = allWork.filter((film) => film.id !== "sumo-didi");

  // 1. Swap positions of ICICI and Vim Neel Gel
  const iciciIndex = commercials.findIndex(c => 
    c.title && (c.title.toLowerCase().includes("icici") || c.title.toLowerCase().includes("click click"))
  );
  const vimIndex = commercials.findIndex(c => 
    c.title && (c.title.toLowerCase().includes("vim") || c.title.toLowerCase().includes("neel gel"))
  );

  if (iciciIndex !== -1 && vimIndex !== -1) {
    const temp = commercials[iciciIndex];
    commercials[iciciIndex] = commercials[vimIndex];
    commercials[vimIndex] = temp;
  }

  // 2. Remove bottom rows from Britannia Treat to CIF
  const britanniaIndex = commercials.findIndex(c => 
    c.title && (c.title.toLowerCase().includes("britannia") || c.title.toLowerCase().includes("treat"))
  );

  const displayedCommercials = britanniaIndex !== -1 
    ? commercials.slice(0, britanniaIndex) 
    : commercials;

  return (
    <div className="bg-white min-h-screen pt-32 pb-24 md:pb-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <h1 className="font-serif text-[32px] md:text-[42px] text-[#1a1a1a] tracking-[-0.02em]">
            Work
          </h1>
          <p className="text-[13px] text-[#999] mt-3 tracking-[0.04em] max-w-[500px] leading-[1.8]">
            A selection of films exploring the unspoken, the overlooked and the quietly resilient.
          </p>
        </motion.div>

        {/* Feature Film Section — Sumo Didi */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          className="mb-24 md:mb-32"
        >
          <p className="text-[14px] md:text-[16px] tracking-[0.18em] uppercase text-[#999] mb-12">
            Feature Film
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
                <SumoDidiMedia />
              </div>
              <div className="lg:col-span-5 lg:py-4">
              <h2 className="font-serif text-[28px] md:text-[36px] text-[#1a1a1a] tracking-[-0.02em]">
                {featureFilm.title}
              </h2>
              <p className="text-[12px] text-[#999] mt-2 tracking-[0.06em]">
                Hindi — {featureFilm.year}
              </p>
              <p className="text-[15px] text-[#4a4a4a] mt-6 leading-[1.8] text-justify">
                {featureFilm.logline}
              </p>
              <div className="mt-8 space-y-6">
                  {featureFilm.festivals.map((f) => (
                    <div key={f.name} className="border-l-2 border-[#d6d0c6] pl-4 py-1">
                      <p className="text-[13px] md:text-[14px] font-medium text-[#1a1a1a] tracking-[0.02em] leading-[1.6]">
                        {f.name}
                      </p>
                      <p className="text-[12px] md:text-[13px] text-[#666] mt-2 leading-[1.7]">
                        {f.note}
                      </p>
                    </div>
                  ))}
                </div>
              <div className="mt-8 flex flex-col items-start gap-5">
                <Link 
                  to={`/work/${featureFilm.id}`} 
                  className="group/link inline-flex min-h-[44px] items-center gap-3 bg-[#1a1a1a] px-6 py-3 text-[12px] font-medium tracking-[0.12em] uppercase text-white hover:bg-[#3a3a3a] transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#1a1a1a]"
                >
                  View Project
                  <ArrowRight size={13} className="transition-transform duration-500 group-hover:translate-x-1" />
                </Link>
                <SumoDidiMediaLinks />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Advertising Showreels Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-24 md:mb-32"
        >
          <p className="text-[14px] md:text-[16px] tracking-[0.18em] uppercase text-[#999] mb-12">
            Advertising Showreels
          </p>
          <div className="flex flex-wrap gap-x-14 gap-y-6">
            {showreels.map((reel, i) => (
              <motion.a
                key={reel.label}
                href={reel.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="group inline-flex items-center gap-3 font-serif text-[26px] md:text-[34px] tracking-[-0.01em] text-[#1a1a1a] transition-colors duration-500"
              >
                <span className="relative inline-block">
                  {reel.label}
                  <span className="pointer-events-none absolute left-0 -bottom-1 h-px w-full origin-left scale-x-0 bg-[#1a1a1a] transition-transform duration-500 ease-out group-hover:scale-x-100" />
                </span>
                <ExternalLink size={16} className="opacity-30 transition-all duration-500 group-hover:opacity-80 group-hover:translate-x-0.5" />
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* All Commercials Section */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-10"
        >
          <p className="text-[14px] md:text-[16px] tracking-[0.18em] uppercase text-[#999] mb-12">
            Extensive Commercials
          </p>
        </motion.div>

        {/* 5 Thumbnails Per Row on large screens (lg:grid-cols-5) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 md:gap-x-6 gap-y-8 md:gap-y-12">
          {displayedCommercials.map((film, i) => (
            <motion.div
              key={film.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.8,
                delay: i * 0.08,
                ease: [0.4, 0, 0.2, 1],
              }}
            >
              <Link to={`/work/${film.id}`} className="group block">
                <div className="aspect-[16/10] overflow-hidden bg-[#f5f4f2] relative">
                  <img
                    src={film.thumbnail}
                    alt={film.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                  />
                  {film.embed && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="absolute inset-0 bg-black/10" />
                      <div className="relative flex items-center justify-center w-10 h-10 md:w-16 md:h-16 rounded-full border border-white/80 bg-black/25 backdrop-blur-[2px] scale-90 group-hover:scale-100 transition-transform duration-500 ease-out">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="white" className="ml-[2px] md:w-4 md:h-4" aria-hidden="true">
                          <path d="M2 1.5v9l8-4.5z" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <h2 className="font-serif text-[15px] sm:text-[16px] md:text-[18px] text-[#1a1a1a] tracking-[-0.01em] leading-snug">
                    {film.title}
                  </h2>
                  {film.duration && (
                    <p className="text-[11px] md:text-[12px] text-[#999] mt-1 tracking-[0.06em]">
                      {film.category} · {film.duration}
                    </p>
                  )}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorkPage;